// VOY-461 — RefinementConstraintSet: the single source of truth for the
// Voy My Trip refinement loop. (Revised after architectural review — see
// the reasoning notes inline; this is the spine every surface depends on.)
//
// THE CONTRACT
//   * ONE active RefinementConstraintSet per result session.
//   * The NL bar ("Tell Voy what to change…") and the filter pills are
//     two EDITORS of this one set. They never hold separate state.
//   * `base` is the IMMUTABLE original framing, captured once at assemble.
//     EVERY refinement — including destination and dates — lives as a
//     constraint layered on top. The live trip is `resolveEffectiveFraming
//     (set)`, a pure projection of constraints over base. This is what
//     makes "undo" possible ("actually keep Honolulu" just drops the
//     destination_override; the pristine base destination returns).
//   * Additive inheritance: applying a constraint replaces only its own
//     slot and keeps everything else. Out-of-sync editors is the named
//     failure mode — both editors render from `constraints[]` and mutate
//     through the helpers here.
//
// MERGE PRIMITIVE: `addConstraint` replaces the same slot with a COMPLETE
// value. Callers compute the complete value: the NL parser (which sees the
// current set) emits the merged list; pills use `toggleInList` to compute
// the new list from a single toggle. One primitive, both editors feed it
// complete values, so they cannot drift.
//
// PROVIDER FACETS ARE MODED, NOT include/exclude pairs: `airline` and
// `hotel_brand` each carry { mode: 'only'|'avoid', … } in ONE slot, so a
// contradiction ("only AA" + "avoid AA") is structurally impossible — the
// latest utterance wins the single slot.
//
// Backend mirror: backend/src/lib/search/refinementConstraints.js keeps the
// same `type` strings + apply semantics. THIS file is canonical.

// ---------------------------------------------------------------
// Constraint types
// ---------------------------------------------------------------

export type ConstraintSource = 'nl' | 'pill';
export type TripComponent = 'flights' | 'hotels' | 'itinerary';
export type CabinClass = 'economy' | 'premium_economy' | 'business' | 'first';
export type FacetMode = 'only' | 'avoid';

/**
 * Typed additive constraints. Each occupies exactly one "slot" (see
 * `sameSlot`). Inventory-changing kinds (destination_override, date_shift,
 * cabin) trigger a supplier RE-SEARCH; the rest REFILTER the fetched set.
 */
export type RefinementConstraint =
  | { type: 'destination_override'; value: string; source: ConstraintSource }
  | { type: 'date_shift'; value: { depart?: string; return?: string; nights_delta?: number }; source: ConstraintSource }
  | { type: 'cabin'; value: CabinClass; source: ConstraintSource }
  | { type: 'airline'; value: { mode: FacetMode; carriers: string[] }; source: ConstraintSource }
  | { type: 'hotel_brand'; value: { mode: FacetMode; brands: string[] }; source: ConstraintSource }
  | { type: 'nonstop_only'; value: boolean; source: ConstraintSource }
  | { type: 'star'; value: number[]; source: ConstraintSource }
  | { type: 'price_ceiling'; value: { component: PriceScope; amount: number }; source: ConstraintSource };

/** Price ceilings apply to bookable components + the trip total (not itinerary). */
export type PriceScope = 'flights' | 'hotels' | 'trip';

export type ConstraintType = RefinementConstraint['type'];

/** The immutable original framing. Refinements layer on top; never mutated. */
export interface RefinementBase {
  destination: string | null;
  origin: string | null;
  depart_date: string | null; // YYYY-MM-DD
  return_date: string | null; // YYYY-MM-DD
  cabin: CabinClass | null;
  pax_adults: number;
  pax_children: number;
  pax_infants: number;
  components: TripComponent[]; // scope (compound-not-package)
  companion_ids: string[] | null;
  vibe: string | null;
  budget_band: string | null;
  trip_plan_id?: string | null;
}

export interface RefinementConstraintSet {
  base: RefinementBase;
  constraints: RefinementConstraint[]; // additive, order-preserving
}

// ---------------------------------------------------------------
// Effective framing — the projection of constraints over base.
// ---------------------------------------------------------------

export interface EffectiveFilters {
  airline?: { mode: FacetMode; carriers: string[] };
  hotelBrand?: { mode: FacetMode; brands: string[] };
  nonstopOnly?: boolean;
  stars?: number[];
  priceCeilings: { flights?: number; hotels?: number; trip?: number };
}

export interface EffectiveFraming {
  destination: string | null;
  origin: string | null;
  depart_date: string | null;
  return_date: string | null;
  cabin: CabinClass | null;
  pax_adults: number;
  pax_children: number;
  pax_infants: number;
  components: TripComponent[];
  companion_ids: string[] | null;
  vibe: string | null;
  budget_band: string | null;
  filters: EffectiveFilters;
}

function addDays(isoDate: string, days: number): string {
  // Pure UTC date math on YYYY-MM-DD — no Date.now / timezone drift.
  const [y, m, d] = isoDate.split('-').map(Number);
  const t = Date.UTC(y, m - 1, d) + days * 86400000;
  const dt = new Date(t);
  const mm = String(dt.getUTCMonth() + 1).padStart(2, '0');
  const dd = String(dt.getUTCDate()).padStart(2, '0');
  return `${dt.getUTCFullYear()}-${mm}-${dd}`;
}

/**
 * Fold the additive constraints over the immutable base to produce the
 * live framing the agent searches with + the filters rerank/refilter use.
 * This is the heart of "one code path": assemble passes an empty set (→
 * base verbatim); refine passes the accumulated set (→ base + overrides).
 */
export function resolveEffectiveFraming(set: RefinementConstraintSet): EffectiveFraming {
  const b = set.base;
  const eff: EffectiveFraming = {
    destination: b.destination,
    origin: b.origin,
    depart_date: b.depart_date,
    return_date: b.return_date,
    cabin: b.cabin,
    pax_adults: b.pax_adults,
    pax_children: b.pax_children,
    pax_infants: b.pax_infants,
    components: [...b.components],
    companion_ids: b.companion_ids,
    vibe: b.vibe,
    budget_band: b.budget_band,
    filters: { priceCeilings: {} },
  };

  for (const c of set.constraints) {
    switch (c.type) {
      case 'destination_override':
        eff.destination = c.value;
        break;
      case 'date_shift': {
        if (c.value.depart) eff.depart_date = c.value.depart;
        if (c.value.return) eff.return_date = c.value.return;
        if (typeof c.value.nights_delta === 'number' && eff.return_date) {
          eff.return_date = addDays(eff.return_date, c.value.nights_delta);
        }
        break;
      }
      case 'cabin':
        eff.cabin = c.value;
        break;
      case 'airline':
        eff.filters.airline = { mode: c.value.mode, carriers: [...c.value.carriers] };
        break;
      case 'hotel_brand':
        eff.filters.hotelBrand = { mode: c.value.mode, brands: [...c.value.brands] };
        break;
      case 'nonstop_only':
        eff.filters.nonstopOnly = c.value;
        break;
      case 'star':
        eff.filters.stars = [...c.value];
        break;
      case 'price_ceiling':
        eff.filters.priceCeilings[c.value.component] = c.value.amount;
        break;
    }
  }
  return eff;
}

// ---------------------------------------------------------------
// Re-search vs refilter (documented mapping)
// ---------------------------------------------------------------

/**
 * Inventory-changing kinds — applying these needs a supplier re-query
 * because DIFFERENT offers exist, not a narrowing of fetched ones.
 *   destination_override → new place
 *   date_shift           → new availability window
 *   cabin                → Duffel returns offers PER cabin_class; a
 *                          business fetch isn't a filter of an economy one
 * Everything else (airline, hotel_brand, nonstop_only, star, price_ceiling)
 * is satisfiable by refiltering/re-ranking the already-fetched set.
 */
const RESEARCH_CONSTRAINTS: ReadonlySet<ConstraintType> = new Set([
  'destination_override',
  'date_shift',
  'cabin',
]);

export function constraintRequiresResearch(type: ConstraintType): boolean {
  return RESEARCH_CONSTRAINTS.has(type);
}

// ---------------------------------------------------------------
// Pure operations (the editors call these)
// ---------------------------------------------------------------

export function createConstraintSet(base: RefinementBase): RefinementConstraintSet {
  return { base, constraints: [] };
}

/** Same-slot = applying the new one should REPLACE the old, not stack. */
function sameSlot(a: RefinementConstraint, b: RefinementConstraint): boolean {
  if (a.type !== b.type) return false;
  if (a.type === 'price_ceiling' && b.type === 'price_ceiling') {
    return a.value.component === b.value.component;
  }
  return true; // one slot per type for everything else (incl. moded facets)
}

/** Additive add/replace. Returns a NEW set (immutable). */
export function addConstraint(
  set: RefinementConstraintSet,
  constraint: RefinementConstraint,
): RefinementConstraintSet {
  const kept = set.constraints.filter((c) => !sameSlot(c, constraint));
  return { base: set.base, constraints: [...kept, constraint] };
}

export function addConstraints(
  set: RefinementConstraintSet,
  constraints: RefinementConstraint[],
): RefinementConstraintSet {
  return constraints.reduce(addConstraint, set);
}

/** The "undo" path — drop a constraint, keep everything else. */
export function removeConstraint(
  set: RefinementConstraintSet,
  type: ConstraintType,
  opts?: { priceComponent?: PriceScope },
): RefinementConstraintSet {
  return {
    base: set.base,
    constraints: set.constraints.filter((c) => {
      if (c.type !== type) return true;
      if (c.type === 'price_ceiling' && opts?.priceComponent) {
        return c.value.component !== opts.priceComponent;
      }
      return false;
    }),
  };
}

export function getConstraint(
  set: RefinementConstraintSet,
  type: ConstraintType,
): RefinementConstraint | undefined {
  return set.constraints.find((c) => c.type === type);
}

/**
 * Toggle a value within a moded list facet (airline carriers / hotel
 * brands) — the pill editor's primitive. Computes the COMPLETE new list
 * from the current set, then feeds addConstraint (or removes the slot when
 * the list empties). Keeps pills and NL writing identical complete values.
 */
export function toggleInList<T extends 'airline' | 'hotel_brand'>(
  set: RefinementConstraintSet,
  type: T,
  mode: FacetMode,
  value: string,
  source: ConstraintSource = 'pill',
): RefinementConstraintSet {
  const existing = getConstraint(set, type);
  let current: string[] = [];
  let sameMode = true;
  if (existing && existing.type === 'airline') {
    current = [...existing.value.carriers];
    sameMode = existing.value.mode === mode;
  } else if (existing && existing.type === 'hotel_brand') {
    current = [...existing.value.brands];
    sameMode = existing.value.mode === mode;
  }
  // Switching mode resets the list to just this value; same mode toggles.
  let next: string[];
  if (!sameMode) {
    next = [value];
  } else if (current.includes(value)) {
    next = current.filter((v) => v !== value);
  } else {
    next = [...current, value];
  }
  if (next.length === 0) return removeConstraint(set, type);
  const constraint = (
    type === 'airline'
      ? { type: 'airline', value: { mode, carriers: next }, source }
      : { type: 'hotel_brand', value: { mode, brands: next }, source }
  ) as RefinementConstraint;
  return addConstraint(set, constraint);
}

// ---------------------------------------------------------------
// Component reactions (drives the mid-refinement reload/HELD UI)
// ---------------------------------------------------------------

export type ComponentReaction = 'reload' | 'hold_recheck' | 'unchanged';
const REACTION_RANK: Record<ComponentReaction, number> = { unchanged: 0, reload: 1, hold_recheck: 2 };

function strongest(a: ComponentReaction, b: ComponentReaction): ComponentReaction {
  return REACTION_RANK[a] >= REACTION_RANK[b] ? a : b;
}

function listsDiffer<T>(a?: T[], b?: T[]): boolean {
  const x = JSON.stringify(a ?? null);
  const y = JSON.stringify(b ?? null);
  return x !== y;
}

/**
 * Per-component reaction going from `prev` to `next`, computed off the
 * EFFECTIVE framing (so removing a destination_override — undo — is
 * detected as a destination change back to base). Precedence:
 * hold_recheck > reload > unchanged (a stale-city hotel can't meaningfully
 * refilter by star, so the destination's hold dominates a star reload).
 *
 * Rules:
 *   destination change → flights RELOAD, hotels HOLD_RECHECK, itinerary HOLD_RECHECK
 *   dates change       → flights RELOAD, hotels RELOAD, itinerary HOLD_RECHECK
 *   cabin change       → flights RELOAD
 *   airline/nonstop    → flights RELOAD (refilter)
 *   star/hotel_brand   → hotels RELOAD (refilter)
 *   price ceiling      → scoped component(s) RELOAD
 */
export function componentReactions(
  prev: RefinementConstraintSet,
  next: RefinementConstraintSet,
): Record<TripComponent, ComponentReaction> {
  const p = resolveEffectiveFraming(prev);
  const n = resolveEffectiveFraming(next);
  const out: Record<TripComponent, ComponentReaction> = {
    flights: 'unchanged',
    hotels: 'unchanged',
    itinerary: 'unchanged',
  };
  const bump = (c: TripComponent, r: ComponentReaction) => {
    out[c] = strongest(out[c], r);
  };

  const destChanged = p.destination !== n.destination;
  const datesChanged = p.depart_date !== n.depart_date || p.return_date !== n.return_date;
  const cabinChanged = p.cabin !== n.cabin;

  if (destChanged) {
    // Different city → hotels must re-search, not just hold (mirrors backend).
    // A new city is a bigger change than new dates, which already reload hotels.
    bump('flights', 'reload');
    bump('hotels', 'reload');
    bump('itinerary', 'hold_recheck');
  }
  if (datesChanged) {
    bump('flights', 'reload');
    bump('hotels', 'reload');
    bump('itinerary', 'hold_recheck');
  }
  if (cabinChanged) bump('flights', 'reload');

  // Filter deltas.
  const pf = p.filters;
  const nf = n.filters;
  if (
    listsDiffer(pf.airline?.carriers, nf.airline?.carriers) ||
    pf.airline?.mode !== nf.airline?.mode
  ) bump('flights', 'reload');
  if (pf.nonstopOnly !== nf.nonstopOnly) bump('flights', 'reload');
  if (
    listsDiffer(pf.hotelBrand?.brands, nf.hotelBrand?.brands) ||
    pf.hotelBrand?.mode !== nf.hotelBrand?.mode
  ) bump('hotels', 'reload');
  if (listsDiffer(pf.stars, nf.stars)) bump('hotels', 'reload');
  if (pf.priceCeilings.flights !== nf.priceCeilings.flights) bump('flights', 'reload');
  if (pf.priceCeilings.hotels !== nf.priceCeilings.hotels) bump('hotels', 'reload');
  if (pf.priceCeilings.trip !== nf.priceCeilings.trip) {
    bump('flights', 'reload');
    bump('hotels', 'reload');
  }

  // Restrict to in-scope components; out-of-scope stay 'unchanged'.
  for (const c of ['flights', 'hotels', 'itinerary'] as TripComponent[]) {
    if (!n.components.includes(c)) out[c] = 'unchanged';
  }
  return out;
}

/**
 * For a component the reactions marked 'reload', does it need a supplier
 * RE-SEARCH (inventory changed) or just a REFILTER of the fetched set?
 * Only meaningful for reload components.
 */
export function researchModeFor(
  prev: RefinementConstraintSet,
  next: RefinementConstraintSet,
  component: TripComponent,
): 'research' | 'refilter' {
  const p = resolveEffectiveFraming(prev);
  const n = resolveEffectiveFraming(next);
  const destChanged = p.destination !== n.destination;
  const datesChanged = p.depart_date !== n.depart_date || p.return_date !== n.return_date;
  const cabinChanged = p.cabin !== n.cabin;
  if (component === 'flights') {
    return destChanged || datesChanged || cabinChanged ? 'research' : 'refilter';
  }
  if (component === 'hotels') {
    return destChanged || datesChanged ? 'research' : 'refilter';
  }
  return 'refilter';
}

/** Convenience: any supplier re-search needed at all for this transition. */
export function needsResearch(
  prev: RefinementConstraintSet,
  next: RefinementConstraintSet,
): boolean {
  const reactions = componentReactions(prev, next);
  return (Object.keys(reactions) as TripComponent[]).some(
    (c) => reactions[c] === 'reload' && researchModeFor(prev, next, c) === 'research',
  );
}
